import { AttachmentDto } from 'src/api/FiretrolApiClient';

export type DesignModelFileKind = 'ifc' | 'fab' | 'cad';

export const FILE_KINDS: DesignModelFileKind[] = ['ifc', 'fab', 'cad'];

export const FILE_KIND_LABELS: Record<DesignModelFileKind, string> = {
  ifc: 'IFC',
  fab: 'FAB',
  cad: 'CAD',
};

export const FILE_KIND_FEEDS: Record<DesignModelFileKind, string> = {
  ifc: 'Feeds the 3D model and line grouping',
  fab: 'Feeds WBS accounts and line grouping',
  cad: 'Feeds piece numbers in the 3D model',
};

export const FILE_REQUIRED_MESSAGES: Record<DesignModelFileKind, string> = {
  ifc: 'An IFC file is required.',
  fab: 'A FAB file is required.',
  cad: 'A CAD file is required.',
};

export const isFileKind = (value: string): value is DesignModelFileKind =>
  (FILE_KINDS as string[]).includes(value);

export const extensionOf = (fileName: string | undefined): string => {
  const dot = fileName?.lastIndexOf('.') ?? -1;
  return dot < 0 ? '' : fileName!.slice(dot + 1).toLowerCase();
};

/** The server types an attachment by its extension, so the name is the fallback when `type` is absent. */
export const fileKindOf = (
  attachment: Pick<AttachmentDto, 'type' | 'name'>,
): DesignModelFileKind | undefined => {
  const type = (attachment.type ?? extensionOf(attachment.name))
    .replace(/^\./, '')
    .toLowerCase();
  return isFileKind(type) ? type : undefined;
};

const FILE_KIND_ARTICLES: Record<DesignModelFileKind, string> = {
  ifc: 'an',
  fab: 'a',
  cad: 'a',
};

export const wrongTypeMessage = (fileName: string, kind: DesignModelFileKind) =>
  `"${fileName}" is not ${FILE_KIND_ARTICLES[kind]} ${FILE_KIND_LABELS[kind]} file (.${kind})`;

export interface IDesignModelFileEdits {
  attachment: AttachmentDto[];
  attachmentsToAdd: AttachmentDto[];
  attachmentsToRemove: AttachmentDto[];
}

export const savedFilesOf = (
  attachments: AttachmentDto[] | undefined,
  kind: DesignModelFileKind,
): AttachmentDto[] =>
  (attachments ?? []).filter((a) => !a.archived && fileKindOf(a) === kind);

export const stagedFileOf = (
  edits: IDesignModelFileEdits,
  kind: DesignModelFileKind,
): AttachmentDto | undefined =>
  edits.attachmentsToAdd.filter((a) => fileKindOf(a) === kind).at(-1);

export const hasActiveFile = (
  edits: IDesignModelFileEdits,
  kind: DesignModelFileKind,
): boolean =>
  !!stagedFileOf(edits, kind) ||
  savedFilesOf(edits.attachment, kind).some(
    (saved) => !edits.attachmentsToRemove.some((r) => r.id === saved.id),
  );

export const stageFile = (
  edits: IDesignModelFileEdits,
  kind: DesignModelFileKind,
  file: AttachmentDto,
): Pick<IDesignModelFileEdits, 'attachmentsToAdd' | 'attachmentsToRemove'> => {
  const saved = savedFilesOf(edits.attachment, kind);
  return {
    attachmentsToAdd: [
      ...edits.attachmentsToAdd.filter((a) => fileKindOf(a) !== kind),
      file,
    ],
    attachmentsToRemove: [
      ...edits.attachmentsToRemove.filter(
        (r) => !saved.some((s) => s.id === r.id),
      ),
      ...saved,
    ],
  };
};

export const unstageFile = (
  edits: IDesignModelFileEdits,
  kind: DesignModelFileKind,
): Pick<IDesignModelFileEdits, 'attachmentsToAdd' | 'attachmentsToRemove'> => {
  const saved = savedFilesOf(edits.attachment, kind);
  return {
    attachmentsToAdd: edits.attachmentsToAdd.filter(
      (a) => fileKindOf(a) !== kind,
    ),
    attachmentsToRemove: edits.attachmentsToRemove.filter(
      (r) => !saved.some((s) => s.id === r.id),
    ),
  };
};

export type SlotState =
  | 'empty'
  | 'uploading'
  | 'stagedNew'
  | 'stagedReplacement'
  | 'saved'
  | 'savedMissing';

export interface ISlotStateInput {
  saved?: AttachmentDto;
  staged?: AttachmentDto;
  missing: boolean;
  uploading: boolean;
  /** Why file changes are blocked (a save in flight, or the model processing); null when they are allowed. */
  lockedReason?: string | null;
  error?: string | null;
  problems?: string[];
}

export interface ISlotView {
  state: SlotState;
  locked: boolean;
  lockedReason: string | null;
  error: string | null;
  hasProblem: boolean;
  canReplace: boolean;
  canUndo: boolean;
  canRemove: boolean;
  canDownload: boolean;
}

export const deriveSlotState = ({
  saved,
  staged,
  missing,
  uploading,
  lockedReason = null,
  error = null,
  problems = [],
}: ISlotStateInput): ISlotView => {
  const state: SlotState = uploading
    ? 'uploading'
    : staged
    ? saved
      ? 'stagedReplacement'
      : 'stagedNew'
    : saved
    ? missing
      ? 'savedMissing'
      : 'saved'
    : 'empty';
  const locked = !!lockedReason;
  const filled = state !== 'empty' && state !== 'uploading';
  return {
    state,
    locked,
    lockedReason: lockedReason || null,
    error: error || null,
    hasProblem: problems.length > 0,
    canReplace: filled && !locked,
    canUndo: state === 'stagedReplacement' && !locked,
    canRemove: state === 'stagedNew' && !locked,
    canDownload: state === 'saved',
  };
};

export const joinFileLabels = (
  kinds: DesignModelFileKind[],
  type: 'conjunction' | 'disjunction' = 'conjunction',
) =>
  new Intl.ListFormat('en-US', { type }).format(
    kinds.map((k) => FILE_KIND_LABELS[k]),
  );

export const missingFilesSentence = (kinds: DesignModelFileKind[]): string =>
  kinds.length === 1
    ? `The ${joinFileLabels(
        kinds,
      )} file is missing from storage. Replace it and save.`
    : `The ${joinFileLabels(
        kinds,
      )} files are missing from storage. Replace them and save.`;

export const toFileKinds = (
  values: string[] | undefined,
): DesignModelFileKind[] =>
  FILE_KINDS.filter((kind) =>
    (values ?? []).some((v) => v.toLowerCase() === kind),
  );
